import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import NotificationService from '@/services/NotificationService';
import { signalRService } from '@/services/SignalRService';
import type { NotificationResponse } from '@/types/notification';
import { useAuthenticationStore } from './authenticationStore';

export const useNotificationStore = defineStore('notification', () => {
  // State
  const notifications = ref<NotificationResponse[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const isSignalRConnected = ref(false);

  // Getters
  const unreadCount = computed(() => 
    notifications.value.filter(n => !n.isRead).length
  );

  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.isRead)
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
  );

  const bookingRelatedUnreadCount = computed(() =>
    notifications.value.filter(n => !n.isRead && n.relatedBookingId !== null).length
  );

  // Actions
  const loadUnreadNotifications = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      notifications.value = await NotificationService.getUnreadNotifications();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load notifications';
      console.error('Failed to load notifications:', err);
    } finally {
      loading.value = false;
    }
  };

  const markAsRead = async (notificationId: number): Promise<void> => {
    try {
      await NotificationService.markAsRead(notificationId);
      
      // Update local state
      const notification = notifications.value.find(n => n.notificationId === notificationId);
      if (notification) {
        notification.isRead = true;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to mark notification as read';
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  };

  const markAllBookingNotificationsAsRead = async (): Promise<void> => {
    const bookingNotifications = notifications.value
      .filter(n => !n.isRead && n.relatedBookingId !== null);
    
    for (const notification of bookingNotifications) {
      try {
        await markAsRead(notification.notificationId);
      } catch (err) {
        console.error(`Failed to mark notification ${notification.notificationId} as read:`, err);
      }
    }
  };

  const connectSignalR = async (): Promise<void> => {
    const authStore = useAuthenticationStore();
    
    if (!authStore.isAuthenticated) {
      console.warn('Cannot connect to SignalR: User not authenticated');
      return;
    }

    try {
      await signalRService.connect();
      isSignalRConnected.value = signalRService.isConnected();

      // Register notification handler
      signalRService.on('ReceiveNotification', (payload: any) => {
        console.log('Received notification via SignalR:', payload);
        
        // Add new notification to the list
        const newNotification: NotificationResponse = {
          notificationId: payload.notificationId,
          userId: authStore.currentUser?.userId || 0,
          notificationTypeLookupId: 0, // Not provided in payload
          title: payload.title,
          message: payload.message,
          isRead: false,
          isSent: true,
          sentOn: new Date().toISOString(),
          relatedBookingId: payload.relatedBookingId || null,
          createdDate: payload.createdOn || new Date().toISOString()
        };

        notifications.value.unshift(newNotification);

        // Optional: Show toast notification
        // const toastStore = useToastStore();
        // toastStore.showInfo(payload.title, payload.message);
      });

      console.log('SignalR notification handler registered');
    } catch (err) {
      console.error('Failed to connect to SignalR:', err);
    }
  };

  const disconnectSignalR = async (): Promise<void> => {
    await signalRService.disconnect();
    isSignalRConnected.value = false;
  };

  const clearNotifications = (): void => {
    notifications.value = [];
  };

  return {
    notifications,
    loading,
    error,
    unreadCount,
    unreadNotifications,
    bookingRelatedUnreadCount,
    isSignalRConnected,
    loadUnreadNotifications,
    markAsRead,
    markAllBookingNotificationsAsRead,
    connectSignalR,
    disconnectSignalR,
    clearNotifications
  };
});
